import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "../utils/http";
import { useSession } from "../context/SessionContext";

// The menu, from denzo-api's app/v1 storefront endpoints — GET `products`
// (ProductController@getStoreProducts, server-paginated via
// getProductQuery/app_page_limit, accepts `search` and `categories`) and GET
// `categories` (StoreController@getCategories). Both require the anonymous
// guest token from SessionContext's register() call, so this hook waits on
// `ready` before fetching anything.
export default function useMenu({ search = "", category = "all" } = {}) {
  const { ready } = useSession();

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [hasMore, setHasMore] = useState(false);

  const pageRef = useRef(1);

  const fetchPage = useCallback(
    async (page, append) => {
      const res = await apiClient.get("products", {
        params: { page, search, categories: category !== "all" ? category : undefined },
      });
      const paginator = res.data?.products || {};
      const newItems = paginator.data || [];
      setProducts((prev) => (append ? [...prev, ...newItems] : newItems));
      setHasMore((paginator.current_page || 1) < (paginator.last_page || 1));
      pageRef.current = page;
    },
    [search, category]
  );

  const loadCategories = useCallback(() => {
    apiClient
      .get("categories")
      .then((res) => setCategories(res.data?.categories || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!ready) return;
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  useEffect(() => {
    if (!ready) return;
    setLoading(true);
    setError("");
    fetchPage(1, false)
      .catch((err) => setError(err.message || "Failed to load menu"))
      .finally(() => setLoading(false));
  }, [ready, fetchPage]);

  const loadMoreProducts = useCallback(() => {
    setLoadingMore(true);
    fetchPage(pageRef.current + 1, true)
      .catch((err) => setError(err.message || "Failed to load menu"))
      .finally(() => setLoadingMore(false));
  }, [fetchPage]);

  const reload = useCallback(() => {
    setLoading(true);
    setError("");
    fetchPage(1, false)
      .catch((err) => setError(err.message || "Failed to load menu"))
      .finally(() => setLoading(false));
  }, [fetchPage]);

  return { loading, loadingMore, error, categories, products, hasMore, loadMoreProducts, reload };
}
